import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Why this fork exists
// ----------------------------------------------------------------------------
// Interior pages on Weidner Classic follow Tower 12's inner-page banner
// (https://www.tower12.com/floor-plans): a full-bleed photo, 50% black
// overlay, and a single centered white uppercase title. The rise
// HeroSolidBG is a solid-primary block with a blurb and CTA — that
// chrome does not match the reference, so this fork replaces the
// layout while keeping the same slot name and `tokenPrefix` instances
// already wired across interior pages.
//
// The background image is static: it is clipped to the section and
// scrolls 1:1 with the page. Do not add a scroll-linked translate
// (the rise home Hero's 30% parallax handler), an oversized 140%
// image wrapper, or `background-attachment: fixed` / `position:fixed`
// pinning. Those are the effects that make interior banners feel like
// they "slide" under the title as the page moves.
//
// Maintenance checklist when re-syncing with the rise parent:
//   1. Diff this file against the rise HeroSolidBG in the sibling checkout.
//   2. Do not reintroduce the solid-primary background or the blurb.
//   3. Keep the photo + overlay + centered title treatment.
//   4. Keep the background image static — no parallax scroll handler,
//      no fixed-attachment / pinned background, no oversized wrapper.
//   5. The mustard CTA is opt-in via `showCta` (Virtual Tours). Do not
//      default it on — other interior pages stay title-only.
//   6. The white hero blurb is opt-in via `showBlurb`. Interior pages in
//      pages.json turn it on with a page-relevant `blurbField` and
//      `blurbDefault`. Do not default `showBlurb` on in this component.
//   7. Keep `tokenPrefix` path construction and hidden schema-lint anchors.
//   8. Run `pnpm run lint:schema` and `pnpm run typecheck`.
// ============================================================================

interface HeroSolidBGProps {
  tokenPrefix?: string;
  title?: string;
  showCta?: boolean;
  ctaLabel?: string;
  ctaUrl?: string;
  showBlurb?: boolean;
  blurbField?: string;
  blurbDefault?: string;
}

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Page banner',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Floor Plans',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function HeroSolidBG({
  tokenPrefix,
  title = 'Page',
  showCta = false,
  ctaLabel = 'View Floor Plans',
  ctaUrl = '/floor-plans',
  showBlurb = false,
  blurbField,
  blurbDefault = '',
}: HeroSolidBGProps) {
  const prefix = tokenPrefix || 'herosolidbg';
  const titlePath = `${prefix}.title`;
  const blurbPath = `${prefix}.blurb`;
  const bgPath = `${prefix}.bg_image`;
  const ctaPath = `${prefix}.cta_link`;

  const bgImage = useSlotGroup(bgPath, bgImageDef);
  const cta = useSlotGroup(ctaPath, ctaLinkDef);

  return (
    <section
      className="interior-hero relative w-full overflow-hidden px-10"
      style={{
        paddingTop: 'calc(var(--offer-banner-height, 0px) + 13.5rem)',
        paddingBottom: showBlurb ? '5.5rem' : '9.25rem',
        transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Static background — clipped to the section, no parallax scroll. */}
      <EditableSlotGroup
        groupPath={bgPath}
        definition={bgImageDef}
        className="absolute inset-0 overflow-hidden"
      >
        {bgImage.image ? (
          <VibesitesImage
            url={bgImage.image}
            alt={bgImage.alt || ''}
            priority
            fill
            sizes="100vw"
            objectFit="cover"
            className="interior-hero__image h-full w-full object-cover object-center"
          />
        ) : (
          <div className="h-full w-full bg-[var(--vibe-color-text)]" />
        )}
        {/* Decorative contrast overlay — pointer-events off so empty
            hero space does not intercept chatbot / widget clicks. */}
        <div className="absolute inset-0 bg-black/50 pointer-events-none" />
      </EditableSlotGroup>

      <div className="relative z-10 flex flex-col items-center justify-center text-center pointer-events-none">
        <RichTextContent
          path={titlePath}
          defaultValue={`<p>${title}</p>`}
          as="h1"
          className="interior-hero__title font-heading text-[1.9375rem] md:text-[2.8125rem] font-bold uppercase leading-none tracking-normal pointer-events-auto"
        />
        {showBlurb ? (
          <RichTextContent
            path={blurbPath}
            defaultField={blurbField}
            defaultValue={blurbDefault || ''}
            as="div"
            className="interior-hero__blurb font-body pointer-events-auto"
          />
        ) : null}
        {showCta ? (
          <EditableSlotGroup
            groupPath={ctaPath}
            definition={ctaLinkDef}
            className="interior-hero__cta-wrap pointer-events-auto"
          >
            <a
              href={cta.url || ctaUrl}
              target={cta._target || '_self'}
              className="interior-hero__cta"
            >
              {cta.label || ctaLabel}
            </a>
          </EditableSlotGroup>
        ) : null}
      </div>

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="herosolidbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="herosolidbg.bg_image"
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="herosolidbg.cta_link"
        definition={ctaLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        /* Kill any inherited Ken Burns / parallax transform so the
           photo stays locked to the banner box as the page scrolls. */
        .interior-hero__image {
          transform: none !important;
          will-change: auto;
        }
        .interior-hero__title,
        .interior-hero__title * {
          color: #fff !important;
        }
        .interior-hero__blurb {
          margin-top: 1.25rem;
          max-width: 52rem;
          font-size: 1rem;
          line-height: 1.5;
          color: #fff;
        }
        .interior-hero__blurb,
        .interior-hero__blurb * {
          color: #fff !important;
        }
        .interior-hero__blurb p {
          margin: 0;
        }
        .interior-hero__cta-wrap {
          margin-top: 1.75rem;
        }
        .interior-hero__cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.85rem 1.75rem;
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          text-decoration: none;
          border: none;
          border-radius: 0;
          transition: background 0.25s ease, color 0.25s ease;
        }
        .interior-hero__cta:hover {
          background: #b6b6b6;
          color: #000;
        }
        @media (max-width: 767px) {
          .interior-hero {
            padding-top: calc(var(--offer-banner-height, 0px) + 8.5rem);
            padding-bottom: 5.5rem;
          }
          .interior-hero__blurb {
            font-size: 0.9375rem;
            max-width: 36rem;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSolidBG',
  displayName: 'Interior Hero',
  tokens: {
    'herosolidbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'herosolidbg.blurb': {
      label: 'Hero Blurb',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'herosolidbg.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Page banner',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
    'herosolidbg.cta_link': ctaLinkDef,
  },
};

(HeroSolidBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSolidBG;
